import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { IoClose, IoSearch } from 'react-icons/io5';

import { SearchBar, ButtonDatePicker } from './';
import Dropdown from '../Structures/Dropdown';
import { Button } from '../ui/Button';
import {
  clearAllFilters,
  setFilterChamber,
  setFilterCommittee,
  setFilterDate,
  setFilterLegislature,
} from '../../features/video/videoSlice';

const FiltersContainer = ({ searchRes, setSearchRes, setLoading }) => {
  const [searchTerm, setSearchTerm] = useState(null);
  const [committeeList, setCommitteeList] = useState(['']);

  const { date, chamber, committee, legislature } = useSelector((s) => s.video.filter);
  const sessions = useSelector((s) => s.user.sessions);
  const dispatch = useDispatch();

  useEffect(() => {
    getCommitteeList();
    handleSearch(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, chamber, committee, legislature]);

  const handleSearch = async (term) => {
    try {
      if (term) {
        let api_url = `/api/video/search?term=${term}`;
        setLoading(true);

        if (date) {
          api_url = api_url + `&month=${date.getMonth() + 1}&year=${date.getFullYear()}`;
        }
        if (chamber) api_url += `&chamber=${chamber}`;
        if (committee) api_url += `&committee=${committee}`;
        if (legislature) api_url += `&legislature=${legislature}`;

        const { data } = await axios.get(api_url);
        setSearchRes(data);
      } else setSearchRes(null);
    } catch (error) {
      console.log(error);
      setSearchRes([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => setSearchTerm('');

  const handleChamberChange = (value) => {
    dispatch(setFilterChamber(chamber === value ? '' : value));
  };

  const getCommitteeList = async () => {
    try {
      const { data } = await axios.get('/api/video/committees');
      setCommitteeList(
        data.data.map((item) => ({
          label: item.CommitteeName,
          value: item.Code,
        }))
      );
    } catch (error) {
      console.log(error);
    }
  };

  const clearFilters = () => {
    dispatch(clearAllFilters());
    clearSearch();
  };

  const handleChangeCommittee = (committee) => dispatch(setFilterCommittee(committee));

  const getLegislatureList = () => {
    const sessionList =
      sessions?.map((session) => {
        if (typeof session === 'object' && session.label && session.value !== undefined) {
          return session;
        }
        return { label: session, value: session };
      }) || [];
    return [{ label: 'All Sessions', value: '' }, ...sessionList];
  };

  const handleChangeLegislature = (legislature) => dispatch(setFilterLegislature(legislature));

  const hasActiveFilter = !!(date || chamber || committee || legislature || searchRes?.length > 1);

  const ChamberPill = ({ value, label }) => {
    const active = chamber === value;
    return (
      <button
        type="button"
        onClick={() => handleChamberChange(value)}
        className={`inline-flex h-9 items-center rounded-md border px-3 text-sm font-medium transition-colors ${
          active
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-card text-foreground hover:bg-muted'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3.5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">Video Transcripts</h2>
        <p className="hidden text-sm text-muted-foreground sm:block">
          Browse, search, and filter recorded committee hearings.
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <ChamberPill value="S" label="Senate" />
          <ChamberPill value="H" label="House" />
          <ChamberPill value="D" label="Agency" />
          <Button onClick={() => handleSearch(searchTerm)}>
            <IoSearch className="h-4 w-4" />
            Search
          </Button>
          {hasActiveFilter && (
            <Button variant="outline" onClick={clearFilters}>
              Clear
              <IoClose className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-3">
        <div className="min-w-0 lg:min-w-[280px] lg:flex-1">
          <SearchBar
            handleSearch={handleSearch}
            search={searchTerm}
            setSearch={setSearchTerm}
            placeholder="AI powered Search…"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ButtonDatePicker
            startDate={date}
            setStartDate={(newDate) => dispatch(setFilterDate(newDate))}
          />
          <div className="w-40">
            <Dropdown
              title="Committee"
              options={committeeList}
              onChange={(e) => handleChangeCommittee(e.target.value)}
              value={committee}
            />
          </div>
          <div className="w-36">
            <Dropdown
              title="Legislature"
              options={getLegislatureList()}
              onChange={(e) => handleChangeLegislature(e.target.value)}
              value={legislature}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltersContainer;
